/** * High-level overview template used for landing pages inside the admin center. * * The component
turns menu definitions into a card grid so that feature areas * such as tenant settings or module
overviews can expose their child pages * without duplicating layout logic. It is intentionally thin
and delegates the * actual information architecture to the central menu configuration. */
<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { MenuCard } from '@/components/atoms'
import { getMenuItems } from '@/composables/menuItems'
import { useSessionStore } from '@/store/sessionStore'
import type { MenuItem } from '@/types'
/**
 * Renders a responsive grid of MenuCards for overview pages.
 *
 * Either `items` or `getItemsFromRouter` must be provided — not both.
 */
const props = defineProps<{
  /**
   * Array of menu items to display as cards.
   * Use this when you want to pass a custom list.
   */
  items?: MenuItem[]
  /**
   * If `true`, items are resolved automatically from the menu config
   * matching the current route path. Ignores `items` when set.
   */
  getItemsFromRouter?: boolean
}>()

const route = useRoute()
const sessionStore = useSessionStore()

const menuItems = computed<MenuItem[]>(() => {
  // Resolve child links from the global menu tree when the current route acts
  // as an overview page for a feature section.
  if (props.getItemsFromRouter) {
    const items = getMenuItems(sessionStore.tenant) as MenuItem[]
    const current = items.find((item) => item.to === route.path)
    return current?.items ?? []
  }
  if (props.items) {
    return props.items
  }
  return []
})
</script>

<template>
  <div>
    <div class="top-content">
      <slot name="top-content" />
    </div>
    <section class="tiles">
      <MenuCard
        v-for="item in menuItems ?? []"
        :key="item.to"
        :setting="{
          label: item.label,
          to: item.to,
          icon: item.icon,
          imageSrc: item.imageSrc,
          identifier: item.identifier
        }"
      />
    </section>
  </div>
</template>
